import { useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { IPaymentFormData, PaymentMethod } from '@typings'
import { PAYMENT_METHOD_OPTIONS, PAYMENT_LABELS } from 'src/constants/payment.constants'
import { formatValueToCurrency } from 'src/utilities/helpers/formatCentToAmount.helper'

interface PaymentFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: IPaymentFormData) => Promise<void>
  defaultValues?: Partial<IPaymentFormData>
  maxAmount?: number
  loading?: boolean
}

export const PaymentForm = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  maxAmount,
  loading = false,
}: PaymentFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IPaymentFormData>({
    defaultValues: {
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'bank_transfer' as PaymentMethod,
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer' as PaymentMethod,
        ...defaultValues,
      })
    }
  }, [open, defaultValues, reset])

  const handleFormSubmit = async (data: IPaymentFormData) => {
    try {
      await onSubmit(data)
      reset()
      onClose()
    } catch (error) {
      console.error('Payment submission error:', error)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>{PAYMENT_LABELS.TITLE}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: 'Bedrag is verplicht',
                min: { value: 1, message: 'Bedrag moet groter dan 0 zijn' },
                max: maxAmount
                  ? {
                      value: maxAmount,
                      message: `Bedrag kan niet meer zijn dan ${formatValueToCurrency(maxAmount)}`,
                    }
                  : undefined,
              }}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.AMOUNT}
                  type="number"
                  fullWidth
                  required
                  error={!!errors.amount}
                  helperText={errors.amount?.message || (maxAmount ? `Max: ${formatValueToCurrency(maxAmount)}` : '')}
                  value={value ? value / 100 : ''}
                  onChange={e => {
                    const euroValue = parseFloat(e.target.value) || 0
                    onChange(Math.round(euroValue * 100))
                  }}
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
                  }}
                />
              )}
            />

            <Controller
              name="payment_date"
              control={control}
              rules={{ required: 'Betaaldatum is verplicht' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.PAYMENT_DATE}
                  type="date"
                  fullWidth
                  required
                  error={!!errors.payment_date}
                  helperText={errors.payment_date?.message}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="payment_method"
              control={control}
              rules={{ required: 'Betaalwijze is verplicht' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.PAYMENT_METHOD}
                  select
                  fullWidth
                  required
                  error={!!errors.payment_method}
                  helperText={errors.payment_method?.message}>
                  {PAYMENT_METHOD_OPTIONS.map(option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.REFERENCE}
                  fullWidth
                  error={!!errors.reference}
                  helperText={errors.reference?.message}
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={PAYMENT_LABELS.NOTES}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isSubmitting || loading}>
            {PAYMENT_LABELS.CANCEL}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || loading}>
            {PAYMENT_LABELS.SAVE}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
